import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  // Load user profile
  const loadUserProfile = async (userId) => {
    if (!userId) return;
    
    setProfileLoading(true);
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();
      
      if (!error && data) {
        setUserProfile(data);
      } else {
        console.error('Profile load error:', error);
        setUserProfile(null);
      }
    } catch (error) {
      console.error('Profile load error:', error);
      setUserProfile(null);
    } finally {
      setProfileLoading(false);
    }
  };

  // Clear user profile
  const clearUserProfile = () => {
    setUserProfile(null);
    setProfileLoading(false);
  };

  useEffect(() => {
    let mounted = true;

    // Get initial session
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      if (mounted) {
        setUser(session?.user ?? null);
        setLoading(false);
        
        if (session?.user) {
          loadUserProfile(session?.user?.id);
        } else {
          clearUserProfile();
        }
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase?.auth?.onAuthStateChange((event, session) => {
      if (mounted) {
        setUser(session?.user ?? null);
        setLoading(false);
        
        if (session?.user) {
          loadUserProfile(session?.user?.id);
        } else {
          clearUserProfile();
        }
      }
    });

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  // Auth methods
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ 
        email, 
        password 
      });
      return { data, error };
    } catch (error) {
      return { 
        data: null, 
        error: { message: 'Network error. Please try again.' } 
      };
    }
  };

  const signInWithGoogle = async () => {
    try {
      const { data, error } = await supabase?.auth?.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location?.origin}/outage-dashboard`
        }
      });
      return { data, error };
    } catch (error) {
      return { 
        data: null, 
        error: { message: 'Network error. Please try again.' } 
      };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut();
      if (!error) {
        setUser(null);
        clearUserProfile();
      }
      return { error };
    } catch (error) {
      return { 
        error: { message: 'Network error. Please try again.' } 
      };
    }
  };

  const updateProfile = async (updates) => {
    if (!user) {
      return { 
        data: null, 
        error: { message: 'No user logged in' } 
      };
    }
    
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single();
      
      if (!error && data) {
        setUserProfile(data);
      }
      return { data, error };
    } catch (error) {
      return { 
        data: null, 
        error: { message: 'Network error. Please try again.' } 
      };
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signInWithGoogle,
    signOut,
    updateProfile,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};